import { differenceInCalendarDays, format, isSameDay, isToday, isTomorrow } from 'date-fns';
import { normalizeApiError } from '@clarix/utils';
import { QM_RECOVERY } from './constants';
import { CHIP_COPY, FAILURE_COPY, MIC_DENIED_STEPS, QUOTA_COPY, START_ERROR_COPY, START_FALLBACK } from './copy';
import type { ChipPerson, RecordingSummary, WireEvent } from './types';

const MIN = 60_000;

/** `12:34` under an hour, `1:02:03` beyond. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

export function formatClock(iso: string | number, h24: boolean): string {
  return format(new Date(iso), h24 ? 'HH:mm' : 'h:mm a');
}

/** "3:00 PM" today, "Thu 3:00 PM" otherwise. */
export function formatDayClock(iso: string, h24: boolean): string {
  const time = formatClock(iso, h24);
  return isToday(new Date(iso)) ? time : `${format(new Date(iso), 'EEE')} ${time}`;
}

export function formatRange(e: WireEvent, h24: boolean): string {
  return `${formatDayClock(e.start, h24)} – ${formatClock(e.end, h24)}`;
}

/** "6:30 – 7:00 PM": the meridiem shows once when both ends share it on the same day. */
export function formatTimeRange(e: WireEvent, h24: boolean): string {
  const start = new Date(e.start);
  const end = new Date(e.end);
  const shared = !h24 && isSameDay(start, end) && format(start, 'a') === format(end, 'a');
  return `${shared ? format(start, 'h:mm') : formatClock(e.start, h24)} – ${formatClock(e.end, h24)}`;
}

/** "Today · 6:30 – 7:00 PM", "Tomorrow · …", "Fri, Oct 10 · …". */
export function formatDayRange(e: WireEvent, h24: boolean): string {
  const start = new Date(e.start);
  const day = isToday(start) ? CHIP_COPY.today : isTomorrow(start) ? CHIP_COPY.tomorrow : format(start, 'EEE, MMM d');
  return `${day} · ${formatTimeRange(e, h24)}`;
}

/** A past meeting's day for the pill: "Today", "Tue" within the week, "Sep 28" beyond it. */
export function formatShortDay(iso: string, now: number): string {
  const days = differenceInCalendarDays(now, new Date(iso));
  if (days === 0) return CHIP_COPY.today;
  return days > 0 && days < 7 ? format(new Date(iso), 'EEE') : format(new Date(iso), 'MMM d');
}

/** "2 h 10 min", "2 h", "45 min". */
export function formatSpan(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return CHIP_COPY.minutes(m);
  return m === 0 ? CHIP_COPY.hours(h) : `${CHIP_COPY.hours(h)} ${CHIP_COPY.minutes(m)}`;
}

/** A quick meeting is the caller's own, so it is never shown as private. */
export const isPrivateMeeting = (e: WireEvent) => e.source !== 'quick' && (e.visibility === 'private' || e.masked);

/** The host first, then everyone who hasn't declined; an outside guest is named by email when nameless. */
export function meetingPeople(e: WireEvent): ChipPerson[] {
  if (isPrivateMeeting(e)) return [];
  const people: ChipPerson[] = e.host ? [{ id: e.host.id, name: e.host.name, avatarUrl: e.host.avatar_url }] : [];
  for (const a of e.attendees ?? []) {
    if (a.rsvp === 'declined') continue;
    if (a.person) people.push({ id: a.person.id, name: a.person.name, avatarUrl: a.person.avatar_url });
    else if (a.external) people.push({ id: `ext:${a.external.email}`, name: a.external.name || a.external.email, avatarUrl: null });
  }
  return people.filter((p, i) => people.findIndex((q) => q.id === p.id) === i);
}

/** Private events never show a title in the shell; a quick meeting reads as one. */
export function eventLabel(e: WireEvent): string {
  if (e.source === 'quick') return CHIP_COPY.quickMeeting;
  if (isPrivateMeeting(e)) return CHIP_COPY.privateMeeting;
  return e.title;
}

export function conferenceAppName(app: string): string | null {
  const names = CHIP_COPY.conferenceApp;
  return (Object.prototype.hasOwnProperty.call(names, app) && names[app]) || null;
}

export interface EventPlace {
  kind: 'room' | 'location';
  /** The room's name alone, or the location: the short form the pill shows. */
  name: string;
  /** "Board room · 3rd floor", or the location. */
  label: string;
  /** The room's floor, shown beside its name in the card. */
  detail: string | null;
}

/** Where a meeting is: its booked room, else the free-text location. Hidden wherever `eventLabel` hides the title. */
export function eventPlace(e: WireEvent): EventPlace | null {
  if (e.visibility === 'private' || e.masked) return null;
  const { room } = e;
  if (room) {
    const full = room.floor ? `${room.name} · ${room.floor}` : room.name;
    const mark = (s: string) => (room.removed ? CHIP_COPY.roomRemoved(s) : s);
    return { kind: 'room', name: mark(room.name), label: mark(full), detail: room.floor };
  }
  const location = e.location?.trim();
  return location ? { kind: 'location', name: location, label: location, detail: null } : null;
}

export const isLive = (e: WireEvent) => e.status === 'InProgress';

/** Whole minutes to the start, rounded up, so "in 1 min" holds until the start itself. */
export const minutesUntil = (e: WireEvent, now: number) => Math.ceil((Date.parse(e.start) - now) / MIN);

/** Copy for a failed `POST /quick-meetings` or recording write. */
export function startErrorMessage(error: unknown): string {
  const { code, details } = normalizeApiError(error);
  if (code === 'RECORDING_QUOTA_EXCEEDED') {
    const scope = (details as { scope?: unknown } | undefined)?.scope;
    const copy = typeof scope === 'string' ? QUOTA_COPY[scope] : undefined;
    if (copy) return copy;
  }
  return START_ERROR_COPY[code] ?? START_FALLBACK;
}

export function errorCode(error: unknown): { status: number; code: string; details: unknown } {
  const { status, code, details } = normalizeApiError(error);
  return { status, code, details };
}

/** Processing past the usual time; the server retries a stuck stage on its own at 15 min. */
export function isSlowProcessing(r: Pick<RecordingSummary, 'stopped_at'>, now: number): boolean {
  const stopped = r.stopped_at ? Date.parse(r.stopped_at) : NaN;
  return Number.isFinite(stopped) && now - stopped > QM_RECOVERY.SLOW_PROCESSING_MS;
}

export function failureReason(r: RecordingSummary): string {
  return (r.failure && FAILURE_COPY[r.failure.code]) || 'Processing failed.';
}

function browserFamily(): 'chromium' | 'safari' | 'firefox' | 'other' {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/(Chrome|Chromium|Edg)\//.test(ua)) return 'chromium';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
}

export const micDeniedSteps = (): string => MIC_DENIED_STEPS[browserFamily()];
